import {
  AcademicCapIcon,
  ArrowRightStartOnRectangleIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  MapIcon,
  NewspaperIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useLocation } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import { Hexagon } from '@/components/Hexagon';
import { Menu, MenuProps } from '@/components/Menu';
import { getTutorials, getTutorialSteps } from '@/components/tutorial/steps';
import { useTutorial } from '@/components/tutorial/TutorialProvider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useGetCollaboratorsListFromAccounts } from '@/hooks/useAccounts';
import { useAxios } from '@/hooks/useAxios';
import { useFlag } from '@/hooks/useFlags';
import { useMy } from '@/hooks/useMy';
import Avatar from '@/sections/topNavBar/Avatar';
import { useAuth } from '@/state/auth';
import { cn } from '@/utils/classname';

function TutorialMenuSection() {
  const [expanded, setExpanded] = useState(false);
  const { startTutorial } = useTutorial();
  const axios = useAxios();
  const { invalidate } = useMy(
    { resource: 'setting', user: true },
    { enabled: false }
  );

  const tutorials = getTutorials();

  const handleTutorialClick = (tutorialId: string) => {
    axios
      .put(
        '/setting',
        { name: 'show-welcome-tutorial', value: 'true' },
        {
          params: { user: 'true' },
        }
      )
      .then(() => invalidate());
    const steps = getTutorialSteps(tutorialId);
    if (steps.length > 0) {
      startTutorial(steps);
    }
  };

  return (
    <>
      <DropdownMenuItem
        className="hover:bg-chariot-background-secondary relative m-[8px] w-[calc(100%-16px)] rounded-[2px] py-2 text-start"
        onSelect={e => e.preventDefault()}
        onClick={() => setExpanded(prev => !prev)}
      >
        <div className="flex w-full items-center gap-2">
          <div className="[&>svg]:text-chariot-text-primary [&>svg]:size-5">
            <AcademicCapIcon />
          </div>
          <span className="text-chariot-text-primary flex-1 text-sm leading-8">
            Tutorials
          </span>
          <ChevronDownIcon
            className={cn(
              'text-chariot-text-secondary size-4 transition-transform',
              expanded && 'rotate-180'
            )}
          />
        </div>
      </DropdownMenuItem>
      {expanded && (
        <>
          <div className="scrollbar-dark max-h-[30vh] overflow-y-auto">
            {tutorials.map(tutorial => (
              <DropdownMenuItem
                key={tutorial.id}
                className="hover:bg-chariot-background-secondary relative m-[8px] w-[calc(100%-16px)] rounded-[2px] py-1.5 text-start"
                onSelect={() => handleTutorialClick(tutorial.id)}
              >
                <div className="flex w-full items-center gap-2 pl-7">
                  <span className="text-chariot-text-primary text-sm leading-7">
                    {tutorial.name}
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
          </div>
          <DropdownMenuSeparator className="border-chariot-stroke m-2 -ml-2 border-t" />
        </>
      )}
    </>
  );
}

export function AccountDropdown() {
  const location = useLocation();
  const {
    friend,
    stopImpersonation,
    me: primaryEmail,
    logout,
    isPraetorianUser,
    ssoDomain,
    userType,
  } = useAuth();

  const { enablePersonalTenancy } = useFlag({ doNotImpersonate: true });

  const { data: accounts = [] } = useMy(
    { resource: 'account' },
    { doNotImpersonate: true, enabled: !isPraetorianUser }
  );

  const { data: mySettings } = useMy(
    { resource: 'setting' },
    { doNotImpersonate: true }
  );

  const collaboratorAccounts = useGetCollaboratorsListFromAccounts(accounts);

  const otherTenantCount = collaboratorAccounts.filter(
    acc => acc.name !== primaryEmail
  ).length;
  const hasMultiTenantAccess =
    !isPraetorianUser &&
    otherTenantCount > (userType === 'sso-customer' ? 0 : 1);

  const showPersonalAccount =
    isPraetorianUser || enablePersonalTenancy || !!ssoDomain;

  const displayNameSetting = mySettings?.find(s => s.name === 'display-name');
  const personalAccountLabel =
    (typeof displayNameSetting?.value === 'string'
      ? displayNameSetting.value
      : '') || primaryEmail;

  const topItems: MenuProps['items'] = [
    ...(!isPraetorianUser && primaryEmail !== 'admin@praetorian.com'
      ? [
          {
            label: 'Organization Settings',
            icon: <UserIcon />,
            to: {
              pathname: AuthenticatedRoute.SETTINGS,
              search: '?tab="orgSettings"',
            },
            relative: 'path' as const,
          } as MenuProps['items'][number],
        ]
      : []),
    ...(showPersonalAccount
      ? [
          {
            label: personalAccountLabel,
            icon: (
              <Avatar
                email={primaryEmail}
                className="size-5 max-w-max scale-125 rounded-full"
              />
            ),
            onClick: () => {
              stopImpersonation(
                AuthenticatedRoute.INSIGHTS,
                false,
                enablePersonalTenancy || !!ssoDomain
              );
            },
          },
        ]
      : []),
    ...(isPraetorianUser || hasMultiTenantAccess
      ? [
          {
            label: isPraetorianUser
              ? 'Customer Management'
              : 'Tenant Management',
            icon: isPraetorianUser ? (
              <div className="flex size-5 max-w-max scale-125 items-center justify-center">
                <img
                  src="/icons/praetorian-icon.svg"
                  alt="Praetorian"
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <UserGroupIcon />
            ),
            onClick: async () => {
              stopImpersonation(
                AuthenticatedRoute.CUSTOMER_MANAGEMENT,
                false,
                enablePersonalTenancy || hasMultiTenantAccess
              );
            },
            relative: 'path' as const,
          },
          {
            label: 'Divider',
            type: 'divider' as const,
          },
        ]
      : []),
    ...(!isPraetorianUser
      ? [
          {
            label: 'Shared Documents',
            icon: <DocumentTextIcon />,
            to: AuthenticatedRoute.FILES,
            relative: 'path' as const,
          },
        ]
      : []),
    {
      label: 'Documentation',
      icon: <BookOpenIcon />,
      to: 'https://docs.praetorian.com',
    },
  ];

  const bottomItems: MenuProps['items'] = [
    {
      label: 'Feedback',
      icon: <ChatBubbleLeftRightIcon />,
      to: 'https://portal.praetorian.com',
    },
    {
      label: 'Roadmap',
      icon: <MapIcon />,
      to: 'https://portal.praetorian.com/roadmap',
    },
    {
      label: 'Changelog',
      icon: <NewspaperIcon />,
      to: 'https://portal.praetorian.com/changelog',
    },
    {
      label: 'Divider',
      type: 'divider' as const,
    },
    {
      label: 'User Profile',
      icon: (
        <Avatar
          email={primaryEmail}
          className="size-5 max-w-max scale-125 rounded-full"
        />
      ),
      ...(isPraetorianUser
        ? {
            onClick: () => {
              stopImpersonation(
                AuthenticatedRoute.CUSTOMER_MANAGEMENT_USER_SETTINGS,
                true
              );
            },
          }
        : {
            to: AuthenticatedRoute.PROFILE,
            relative: 'path' as const,
          }),
    },
    {
      label: 'Divider',
      type: 'divider' as const,
    },
    {
      label: 'Log Out',
      icon: <ArrowRightStartOnRectangleIcon />,
      onClick: logout,
    },
  ];

  return (
    <div data-testid="account-dropdown" data-tutorial="account-menu">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="rounded-[2px] p-0 text-base">
            <Hexagon>
              {location.pathname.includes(
                AuthenticatedRoute.CUSTOMER_MANAGEMENT
              ) && isPraetorianUser ? (
                <img
                  src="/icons/logo.png"
                  alt="Praetorian"
                  className="h-full w-full scale-100 object-contain"
                />
              ) : (
                <Avatar
                  className="scale-100"
                  email={friend || primaryEmail}
                  isOrganization={!!friend}
                />
              )}
            </Hexagon>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-64 p-0" sideOffset={10} align="end">
          <Menu items={topItems} />
          <TutorialMenuSection />
          <Menu items={bottomItems} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
